/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `CalendarProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface CalendarProps {
    /**
     * A string representing the CSS class to be applied to the CalendarIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#fff'
     */
    color1?: string;
    /**
     * A number or string representing the height of the CalendarIcon element.
     *
     * @default 18
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Calendar'
     */
    testId?: string;
    /**
     * A number or string representing the height of the CalendarIcon element.
     *
     * @default 18
     */
    width?: number | string;
}

/**
 * The `Calendar` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CalendarProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CalendarIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CalendarIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CalendarIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CalendarComponent = <Calendar className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Calendar = ({className = '', color1 = '#fff', height = 18, ref, testId = 'Calendar', width = 18}: CalendarProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 18 18"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M14.25 3H3.75c-.83 0-1.5.67-1.5 1.5V15c0 .83.67 1.5 1.5 1.5h10.5c.83 0 1.5-.67 1.5-1.5V4.5c0-.83-.67-1.5-1.5-1.5M12 1.5v3m-6-3v3m-3.75 3h13.5" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
);

Calendar.displayName = 'Calendar';